import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Edit2, Trash2, Save, X, User } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/api';

const NotesTimeline = ({ leadId, notes, onNotesUpdate }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [editContent, setEditContent] = useState('');

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) {
      toast.error('Note content cannot be empty');
      return;
    }

    try {
      await api.post(`/leads/${leadId}/notes`, { content: newNoteContent });
      toast.success('Note added successfully');
      setNewNoteContent('');
      setIsAdding(false);
      onNotesUpdate();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleEditNote = (note) => {
    setEditingNoteId(note.note_id);
    setEditContent(note.content);
  };

  const handleSaveEdit = async (noteId) => {
    if (!editContent.trim()) {
      toast.error('Note content cannot be empty');
      return;
    }

    try {
      await api.put(`/leads/${leadId}/notes/${noteId}`, { content: editContent });
      toast.success('Note updated successfully');
      setEditingNoteId(null);
      onNotesUpdate();
    } catch (error) {
      toast.error('Failed to update note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      await api.delete(`/leads/${leadId}/notes/${noteId}`);
      toast.success('Note deleted successfully');
      onNotesUpdate();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const sortedNotes = [...notes].sort((a, b) => 
    new Date(b.created_at) - new Date(a.created_at)
  );

  return (
    <div className="space-y-4" data-testid="notes-timeline">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Notes Timeline</h3>
        {!isAdding && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setIsAdding(true)}
            className="gap-2"
            data-testid="add-note-button"
          >
            <Plus className="h-4 w-4" />
            Add Note
          </Button>
        )}
      </div>

      {isAdding && (
        <Card className="border-primary/50">
          <CardContent className="pt-4 space-y-3">
            <Textarea
              value={newNoteContent}
              onChange={(e) => setNewNoteContent(e.target.value)}
              placeholder="Enter note content..."
              rows={3}
              autoFocus
              data-testid="new-note-textarea"
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAddNote} data-testid="save-note-button">
                <Save className="h-4 w-4 mr-2" />
                Save Note
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setIsAdding(false);
                  setNewNoteContent('');
                }}
                data-testid="cancel-note-button"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {sortedNotes.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            No notes yet. Add your first note above.
          </p>
        ) : (
          sortedNotes.map((note, index) => (
            <Card key={note.note_id} className="relative" data-testid={`note-${index}`}>
              <CardContent className="pt-4">
                {editingNoteId === note.note_id ? (
                  <div className="space-y-3">
                    <Textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      rows={3}
                      autoFocus
                      data-testid="edit-note-textarea"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleSaveEdit(note.note_id)}
                        data-testid="save-edit-button"
                      >
                        <Save className="h-4 w-4 mr-2" />
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingNoteId(null)}
                        data-testid="cancel-edit-button"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                        <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                          {note.author_name && (
                            <span className="flex items-center gap-1 font-medium text-primary">
                              <User className="h-3 w-3" />
                              {note.author_name}
                            </span>
                          )}
                          <span>
                            {formatDate(note.created_at)}
                          </span>
                          {note.updated_at !== note.created_at && (
                            <span className="italic">
                              (edited {formatDate(note.updated_at)})
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditNote(note)}
                          data-testid={`edit-note-${index}`}
                        >
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteNote(note.note_id)}
                          data-testid={`delete-note-${index}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default NotesTimeline;
